import moment from 'moment'
import validator from 'validator'
import {
  addressCoverage,
  employmentGaps,
  isValidLicenceTail,
} from './applicationHistory'

const isAnswered = (value) => typeof value === 'boolean'

// Everything still needed before the application can be submitted, as
// { name, step } where step is a moduleList key.
const submitRequirementChecker = (employeeApplication) => {
  const { employee } = employeeApplication
  const requiredFields = []
  const need = (step) => (name) => requiredFields.push({ name, step })

  // *** PERSONAL DETAILS *** //
  const personnelInfo = employee?.personnelInfo ?? {}
  const { emergencyContact } = personnelInfo
  const personal = need('personal')
  !personnelInfo.dob && personal('Date of birth')
  ;(!emergencyContact?.firstName || !emergencyContact?.phone1) &&
    personal('Emergency contact name and phone number')

  // *** ADDRESS *** //
  const address = need('address')
  ;(!personnelInfo.address?.lineOne || !personnelInfo.address?.postCode) &&
    address('Current address')
  const { currentAddressSince, addressHistory } = employeeApplication
  if (!currentAddressSince) {
    address('Date you moved to your current address')
  } else {
    const { needsHistory, isCovered } = addressCoverage(
      currentAddressSince,
      addressHistory,
    )
    needsHistory && !isCovered && address('Previous addresses (last 5 years)')
  }
  ;(addressHistory ?? []).some(
    (previous) => !previous?.lineOne || !previous?.postCode || !previous?.from,
  ) && address('Incomplete previous address')

  // *** RIGHT TO WORK *** //
  !employeeApplication?.rightToWork?.citizenship &&
    need('rightToWork')('Right to work')

  // *** EMPLOYMENT HISTORY *** //
  const employment = need('employment')
  const jobs = employeeApplication?.workExperience ?? []
  if (!employeeApplication?.noEmploymentHistory) {
    !jobs.length && employment('Employment history')
    jobs.forEach((job, index) => {
      const label = job?.employerName || `Job ${index + 1}`
      ;(!job?.employerName || !job?.jobTitle || !job?.startDate) &&
        employment(`${label}: employer, job title and start date`)
      !job?.stillPresent &&
        !job?.endDate &&
        employment(`${label}: end date`)
      job?.startDate &&
        job?.endDate &&
        !job?.stillPresent &&
        moment(job.endDate).isBefore(moment(job.startDate)) &&
        employment(`${label}: end date is before start date`)
    })
    employmentGaps(jobs).length > 0 &&
      !employeeApplication?.employmentGapsExplanation?.trim() &&
      employment('Explanation of employment gaps')
  }

  // *** SUPPORTING STATEMENT *** //
  !employeeApplication?.personalStatement?.trim() &&
    need('statement')('Supporting statement')

  // *** QUALIFICATIONS *** //
  const { qualifications } = employeeApplication
  const quals = need('qualifications')
  !employee?.clinicalRole && quals('Role applying for')
  !isAnswered(qualifications?.isHCP) &&
    quals('Are you a Paramedic, Nurse or Doctor?')
  if (qualifications?.isHCP === true) {
    !employee?.registrationNumber?.body && quals('Registration body')
    !employee?.registrationNumber?.number && quals('Registration number')
    ;(!employee?.registrationNumber?.renewDate ||
      !moment(employee.registrationNumber.renewDate).isValid()) &&
      quals('Registration renewal date')
  }

  // *** DBS *** //
  !isAnswered(employeeApplication?.dbs?.has3month) &&
    need('dbs')('Recent DBS certificate')

  // *** DRIVING *** //
  const { driving } = employeeApplication
  const drive = need('driving')
  !isAnswered(driving?.hasValidLicence) && drive('Driving licence')
  if (driving?.hasValidLicence) {
    !isValidLicenceTail(driving?.licenceLastEight) &&
      drive('Last 8 characters of your licence number')
    !driving?.checkCode && drive('DVLA check code')
    !isAnswered(driving?.hasC1) && drive('C1 entitlement')
    !isAnswered(driving?.hasBlueLight) && drive('Blue light qualification')
  }

  // *** REFERENCES *** //
  const references = need('references')
  ;['reference1', 'reference2'].forEach((key, index) => {
    const reference = employeeApplication?.[key]
    if (!reference?.fullName || !reference?.email) {
      references(`Reference ${index + 1}: name and email`)
    } else if (!validator.isEmail(reference.email.trim())) {
      references(`Reference ${index + 1}: email address is not valid`)
    }
  })

  return requiredFields
}

export default submitRequirementChecker
